import { Popover, Tooltip } from 'antd';
import Button from 'components/common/Button';
import EditableCell, {
  EDITABLE_NUMBER_CELL_ATTR,
} from 'components/common/EditableCell';
import NumericInputField from 'components/common/NumericInputField';
import {
  NUTRITION_RATE_REQUIRED_MESSAGE,
  NUTRITION_TARGET_REQUIRED_MESSAGE,
} from 'constants/messageConstants';
import {
  NUTRITION_ADD_BUTTON_LABEL,
  NUTRITION_ADD_COLUMN_PRICE,
  NUTRITION_ADD_COLUMN_PRODUCT,
  NUTRITION_ADD_COLUMN_RATE,
  NUTRITION_ADD_COLUMN_UNIT,
  NUTRITION_ADD_MORE_PRODUCTS_LABEL,
  NUTRITION_ADD_PRODUCT_FOR_LABEL,
  NUTRITION_ADD_PRODUCT_LABEL,
  NUTRITION_ADD_TO_LABEL,
  NUTRITION_EDIT_RATE_LABEL,
  NUTRITION_NEW_APPLICATION_DATE_LABEL,
  NUTRITION_NEW_APPLICATION_TIMING_LABEL,
  NUTRITION_REMOVE_CHOICE_LABEL,
  NUTRITION_TIMING_PLACEHOLDER,
  SOILINPUT_STEP_GROUP_KEY,
  TASKS_GRID_SELECT_UNIT_PLACEHOLDER,
} from 'constants/uiConstants';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import type { IItem, IUnit } from 'interfaces/IInput';
import { InputStepType } from 'interfaces/IInputStep';
import { InputTaskType } from 'interfaces/IInputTask';
import React, { useMemo, useRef, useState } from 'react';
import { LuPlus, LuX } from 'react-icons/lu';
import { useSelector } from 'react-redux';
import { formatCurrency } from 'utils/CurrencyUtils';
import { selectActiveGOrgId } from '../../../../../redux/selectors/GlobalCatalogueSelectors';
import { getRateUnitOptions } from 'pages/FarmsPage/Workspace/Tasks/stepCalculations';
import { getStepOptionId } from 'pages/FarmsPage/Workspace/Tasks/stepConfig';
import type { StepSelection } from 'pages/FarmsPage/Workspace/Tasks/stepFactory';
import StepInputSelector from 'pages/FarmsPage/Workspace/Tasks/StepInputSelector';
import { formatDate } from 'pages/FarmsPage/Workspace/Tasks/taskFactory';
import { useTimingCatalogue } from 'pages/FarmsPage/Workspace/Tasks/useTimingCatalogue';
import type { NutritionPlanAudit } from './computeNutritionAudit';
import { defaultTaskTarget, NEW_APPLICATION_TARGET } from './nutritionIntents';
import TaskTargetCards from './TaskTargetCards';
import TipContent from './TipContent';
import type { NutritionProgramEdit } from './useNutritionProgramEdit';

interface AddProductControlProps {
  plan: NutritionPlanAudit;
  /** "North paddock, 2025" — for messages and the button's label. */
  location: string;
  edit: NutritionProgramEdit;
}

/** Fertilisers only — the picker shows no other tabs here. */
export const FERTILISER_CATEGORY_KEYS = [SOILINPUT_STEP_GROUP_KEY];

const FIELD_LABEL = 'text-sm font-semibold text-gray-dark';
// Every field is the same 2rem box as the lens's rate fields, so the form
// reads like the grid it adds to.
const FIELD_BOX =
  'h-8 w-full rounded border border-gray-medium bg-white text-base text-charcoal';
// Product · Rate · Unit · Price · remove.
const PRODUCT_GRID =
  'grid grid-cols-[minmax(8rem,1fr)_6rem_6rem_7rem_2rem] items-center gap-2';

/** Marks the rate and price fields so Up / Down can move between products. */
const ADD_FIELD_ATTR = 'data-nutrition-add-field';

/** Up / Down in a rate or price moves to the same field on the product above or below. */
const focusAdjacentField = (direction: 'up' | 'down', from: HTMLElement) => {
  const current = from.closest<HTMLElement>(`[${ADD_FIELD_ATTR}]`);
  const form = from.closest('form');
  if (!current || !form) return;
  const column = current.getAttribute(ADD_FIELD_ATTR);
  const fields = Array.from(
    form.querySelectorAll<HTMLElement>(`[${ADD_FIELD_ATTR}="${column}"]`)
  );
  const next = fields[fields.indexOf(current) + (direction === 'up' ? -1 : 1)];
  next?.querySelector<HTMLElement>(`[${EDITABLE_NUMBER_CELL_ATTR}]`)?.focus();
};

interface Choice {
  selection: StepSelection;
  rate: number | null;
  unit: IUnit | null;
  cost: number | null;
}

const choiceId = (selection: StepSelection) =>
  getStepOptionId({ ...selection, label: selection.item });

/**
 * "+ Add product" on a plan (OP-2523): pick one or more fertilisers from the
 * catalogue (the Tasks grid's picker, fertiliser list only, multi-select as
 * in Planning), then each one's rate, unit and price, and which planned
 * application or sowing they go on — or a new application, with its date
 * and timing. The choice is prefilled when the plan has only one.
 */
const AddProductControl: React.FC<AddProductControlProps> = ({
  plan,
  location,
  edit,
}) => {
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(true);
  const [choices, setChoices] = useState<Choice[]>([]);
  const [target, setTarget] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [endDateHasDay, setEndDateHasDay] = useState(true);
  const [timing, setTiming] = useState<IItem | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  // The picker closes itself straight after a pick; that close moves on to
  // the details step instead of closing the popover.
  const justPickedRef = useRef(false);

  const gOrgId = useSelector(selectActiveGOrgId);
  const { data: gUnits = [] } = useFetchGUnits(gOrgId);
  const unitOptions = useMemo(
    () => getRateUnitOptions(InputStepType.SoilInput, gUnits),
    [gUnits]
  );
  const timingCatalogue = useTimingCatalogue(open ? gOrgId : null);
  const timingOptions = useMemo(
    () =>
      timingCatalogue.items.map((t) => ({ value: t.globalId, label: t.name })),
    [timingCatalogue.items]
  );

  const lockReason = edit.planLockReason(plan.inputId);
  const saving = edit.isPlanSaving(plan.inputId);
  const targets = edit.taskTargets(plan.inputId);
  const isNew = target === NEW_APPLICATION_TARGET;

  const close = () => {
    setOpen(false);
    setPicking(true);
    setChoices([]);
    setTarget(null);
    setEndDate(null);
    setEndDateHasDay(true);
    setTiming(null);
    setShowErrors(false);
  };

  const pick = (selections: StepSelection[]) => {
    if (selections.length === 0) return;
    justPickedRef.current = true;
    setChoices((prev) => [
      ...prev,
      // Picking a product already listed doesn't add it twice.
      ...selections
        .filter(
          (s) => !prev.some((c) => choiceId(c.selection) === choiceId(s))
        )
        .map((selection) => ({
          selection,
          rate: selection.rate ?? null,
          unit: selection.unit ?? null,
          cost: selection.cost ?? null,
        })),
    ]);
    setTarget((current) => current ?? defaultTaskTarget(targets));
    setPicking(false);
  };

  const updateChoice = (index: number, patch: Partial<Choice>) =>
    setChoices((prev) =>
      prev.map((choice, i) => (i === index ? { ...choice, ...patch } : choice))
    );

  const removeChoice = (index: number) => {
    const next = choices.filter((_, i) => i !== index);
    setChoices(next);
    if (next.length === 0) setPicking(true);
  };

  const submit = () => {
    const missingRate = choices.some((c) => c.rate == null || c.rate < 0);
    if (missingRate || !target) {
      setShowErrors(true);
      return;
    }
    const sent = edit.addProduct({
      inputId: plan.inputId,
      location,
      target,
      products: choices.map((c) => ({
        selection: c.selection,
        rate: c.rate ?? 0,
        unit: c.unit,
        cost: c.cost,
      })),
      newApplication: isNew ? { endDate, endDateHasDay, timing } : undefined,
    });
    if (sent) close();
  };

  const targetMissing = showErrors && !target;

  const details = (
    <form
      className="flex w-[40rem] max-w-[calc(100vw-2rem)] flex-col gap-4 rounded-lg bg-white p-4 shadow-lg"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="flex flex-col gap-2">
        <div className={`${PRODUCT_GRID} ${FIELD_LABEL}`}>
          <span>{NUTRITION_ADD_COLUMN_PRODUCT}</span>
          <span>{NUTRITION_ADD_COLUMN_RATE}</span>
          <span>{NUTRITION_ADD_COLUMN_UNIT}</span>
          <span>{NUTRITION_ADD_COLUMN_PRICE}</span>
          <span />
        </div>
        {choices.map((choice, index) => {
          const rateMissing =
            showErrors && (choice.rate == null || choice.rate < 0);
          return (
            <div key={choiceId(choice.selection)} className="flex flex-col gap-1">
              <div className={PRODUCT_GRID}>
                <span className="min-w-0 truncate text-base font-semibold text-charcoal">
                  {choice.selection.item}
                </span>
                <span
                  {...{ [ADD_FIELD_ATTR]: 'rate' }}
                  aria-label={NUTRITION_EDIT_RATE_LABEL(choice.selection.item)}
                  className={`nutrition-rate-field flex items-center text-right tabular-nums ${FIELD_BOX} ${rateMissing ? 'border-danger' : ''}`}
                >
                  <NumericInputField
                    className="h-full w-full"
                    inputClassName="h-full w-full rounded-[0.1875rem] border-0"
                    value={choice.rate}
                    min={0}
                    inclusiveMin
                    onArrowNavigate={focusAdjacentField}
                    onCommit={(value) => updateChoice(index, { rate: value })}
                  />
                </span>
                <span className={`flex items-center ${FIELD_BOX}`}>
                  <EditableCell
                    type="select"
                    className="h-full w-full rounded"
                    value={choice.unit?.globalId ?? null}
                    displayValue={choice.unit?.unit ?? ' '}
                    options={unitOptions}
                    placeholder={TASKS_GRID_SELECT_UNIT_PLACEHOLDER}
                    onCommit={(globalId) => {
                      const unit = gUnits.find((u) => u.globalId === globalId);
                      if (unit) {
                        updateChoice(index, {
                          unit: { unit: unit.code, globalId: unit.globalId },
                        });
                      }
                    }}
                  />
                </span>
                <span
                  {...{ [ADD_FIELD_ATTR]: 'price' }}
                  className={`nutrition-rate-field flex items-center text-right tabular-nums ${FIELD_BOX}`}
                >
                  <NumericInputField
                    className="h-full w-full"
                    inputClassName="h-full w-full rounded-[0.1875rem] border-0"
                    value={choice.cost}
                    displayValue={
                      choice.cost == null ? ' ' : formatCurrency(choice.cost)
                    }
                    min={0}
                    inclusiveMin
                    allowEmpty
                    onArrowNavigate={focusAdjacentField}
                    onCommit={(value) => updateChoice(index, { cost: value })}
                  />
                </span>
                <button
                  type="button"
                  aria-label={NUTRITION_REMOVE_CHOICE_LABEL(
                    choice.selection.item
                  )}
                  className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded text-gray-dark hover:bg-gray-bright"
                  onClick={() => removeChoice(index)}
                >
                  <LuX size={14} aria-hidden />
                </button>
              </div>
              {rateMissing && (
                <span className="text-sm text-danger-dark">
                  {NUTRITION_RATE_REQUIRED_MESSAGE}
                </span>
              )}
            </div>
          );
        })}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="inline-flex items-center gap-1 self-start px-2"
          onClick={() => setPicking(true)}
        >
          <LuPlus size={14} aria-hidden />
          {NUTRITION_ADD_MORE_PRODUCTS_LABEL}
        </Button>
      </div>

      <div className="flex flex-col gap-1">
        <span className={FIELD_LABEL}>{NUTRITION_ADD_TO_LABEL}</span>
        <TaskTargetCards
          label={NUTRITION_ADD_TO_LABEL}
          targets={targets}
          value={target}
          invalid={targetMissing}
          onChange={setTarget}
        />
        {targetMissing && (
          <span className="text-sm text-danger-dark">
            {NUTRITION_TARGET_REQUIRED_MESSAGE}
          </span>
        )}
      </div>

      {isNew && (
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <span className={FIELD_LABEL}>
              {NUTRITION_NEW_APPLICATION_DATE_LABEL}
            </span>
            <span className={`flex items-center ${FIELD_BOX}`}>
              <EditableCell
                type="date"
                className="h-full w-full rounded"
                value={endDate}
                hasDay={endDateHasDay}
                displayValue={formatDate(endDate, endDateHasDay)}
                onCommit={(value, hasDay) => {
                  setEndDate(value);
                  setEndDateHasDay(hasDay);
                }}
              />
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className={FIELD_LABEL}>
              {NUTRITION_NEW_APPLICATION_TIMING_LABEL}
            </span>
            <span className={`flex items-center ${FIELD_BOX}`}>
              <EditableCell
                type="select"
                className="h-full w-full rounded"
                value={timing?.globalId ?? null}
                displayValue={timing?.item ?? ' '}
                options={timingOptions}
                placeholder={NUTRITION_TIMING_PLACEHOLDER}
                allowClear
                remoteSearch={{
                  searchValue: timingCatalogue.searchTerm,
                  onSearch: timingCatalogue.onSearch,
                  loading: timingCatalogue.isLoading,
                  isPaginating: timingCatalogue.isPaginating,
                  hasNextPage: !!timingCatalogue.hasNextPage,
                  onLoadMore: timingCatalogue.fetchNextPage,
                }}
                onCommit={(globalId) => {
                  const selected = timingCatalogue.items.find(
                    (t) => t.globalId === globalId
                  );
                  setTiming(
                    selected
                      ? { item: selected.name, globalId: selected.globalId }
                      : null
                  );
                }}
              />
            </span>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          variant="success"
          className="px-4 py-1.5 font-bold"
          disabled={saving}
        >
          {NUTRITION_ADD_BUTTON_LABEL}
        </Button>
      </div>
    </form>
  );

  // The shared Button doesn't pass a ref, so the popover anchors to this
  // span instead — without it the popover has nothing to measure and opens
  // in the wrong place (as AddInputControl does in the Tasks grid).
  const button = (
    <span className="inline-flex">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={!!lockReason}
        aria-label={NUTRITION_ADD_PRODUCT_FOR_LABEL(plan.cropName, location)}
        className="inline-flex items-center gap-1 px-2 font-normal"
      >
        <LuPlus size={14} aria-hidden />
        {NUTRITION_ADD_PRODUCT_LABEL}
      </Button>
    </span>
  );

  if (lockReason) {
    return <Tooltip title={<TipContent tip={lockReason} />}>{button}</Tooltip>;
  }

  return (
    <Popover
      trigger="click"
      // Beside the button, as in the Tasks grid: the picker is sized to the
      // window, so only a side placement can shift it to stay on screen.
      placement="right"
      autoAdjustOverflow
      destroyOnHidden
      getPopupContainer={() => document.body}
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : close())}
      classNames={{ root: 'step-input-selector-popover' }}
      content={
        picking ? (
          <StepInputSelector
            taskType={InputTaskType.Application}
            initialCategoryKey={SOILINPUT_STEP_GROUP_KEY}
            categoryKeys={FERTILISER_CATEGORY_KEYS}
            enableMultiSelect
            onClose={() => {
              if (justPickedRef.current) justPickedRef.current = false;
              else if (choices.length > 0) setPicking(false);
              else close();
            }}
            onSelect={pick}
          />
        ) : (
          details
        )
      }
    >
      {button}
    </Popover>
  );
};

export default AddProductControl;
